import React, { useEffect, useRef, useState } from "react";
import {
    Box, Button, TextField, Typography, Divider, Paper, Link, useMediaQuery, CircularProgress,
    Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
    Tabs, Tab, Grid, InputAdornment, IconButton, Chip
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import CloseIcon from '@mui/icons-material/Close';

import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DiscountIcon from "@mui/icons-material/Discount";

import { useNavigate, useLocation } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from '../../contexts/SnackbarContext';

import TermoUsoConteudo from "../Legal/TermoUso";

import api from '../../services/api';
import { getApiErrorMessage, normalizeTeacher } from '../../services/apiUtils';

function isNomeValido(nome) {
    return /^[A-Za-zÀ-ÿ\s]+$/.test(nome);
}

function onlyLettersSpaces(value = "") {
    return String(value)
        .replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g, "") // só letras e espaço
        .replace(/\s{2,}/g, " ")              // sem espaços duplos
        .trimStart();                         // evita começar com espaço
}

function toTitleCaseNome(value = "") {
    const cleaned = onlyLettersSpaces(value).trim();
    if (!cleaned) return "";

    return cleaned
        .toLowerCase()
        .split(" ")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
}


function mascaraCpfCnpj(value = "") {
    value = value.replace(/\D/g, "");
    return value
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
        .slice(0, 14);
}

function mascaraCEP(value = "") {
    return value
        .replace(/\D/g, "")
        .slice(0, 8)
        .replace(/(\d{5})(\d)/, "$1-$2");
}

function mascaraCelular(value = "") {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 10) {
        return digits
            .replace(/^(\d{0,2})/, "($1")
            .replace(/^\((\d{2})(\d)/, "($1) $2")
            .replace(/(\d{4})(\d)/, "$1-$2");
    }
    return digits
        .replace(/^(\d{0,2})/, "($1")
        .replace(/^\((\d{2})(\d)/, "($1) $2")
        .replace(/(\d{5})(\d)/, "$1-$2");
}

export default function Register() {
    const theme = useTheme();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const { login } = useAuth();
    const { showSnackbar } = useSnackbar();

    // Campos existentes
    const [nome, set_nome] = useState("");
    const [email, set_email] = useState("");
    const [cpf_cnpj, set_cpf_cnpj] = useState("");
    const [senha, set_senha] = useState("");
    const [confirmar_senha, set_confirmar_senha] = useState("");

    const passwordValue = senha.trim();

    const passwordRules = [
        {
            key: "minLength",
            valid: passwordValue.length >= 6,
            message: "mínimo de 6 caracteres",
        },
        {
            key: "hasLetter",
            valid: /[A-Za-zÀ-ÿ]/.test(passwordValue),
            message: "pelo menos uma letra",
        },
        {
            key: "hasNumber",
            valid: /\d/.test(passwordValue),
            message: "pelo menos um número",
        },
    ];

    const pendingPasswordRules = passwordRules.filter((rule) => !rule.valid);

    const isPasswordValid = pendingPasswordRules.length === 0;

    const passwordHelperText =
        senha.length === 0
            ? ""
            : isPasswordValid
                ? "Senha válida."
                : `Ainda falta: ${pendingPasswordRules.map((rule) => rule.message).join(", ")}.`;

    // Novos campos
    const [cep, set_cep] = useState("");
    const [endereco, set_endereco] = useState("");
    const [numero, set_numero] = useState("");
    const [complemento, set_complemento] = useState("");
    const [celular, set_celular] = useState("");
    const [bairro, set_bairro] = useState("");
    const [cidade, set_cidade] = useState("");
    const [uf, set_uf] = useState("");
    const [indicacaoRef, set_indicacaoRef] = useState("");

    const [indicacaoBloqueada, setIndicacaoBloqueada] = useState(false);

    const [cupomInfo, setCupomInfo] = useState({
        loading: false,
        valid: false,
        descontoPercentual: 0,
        indicacaoRef: "",
    });

    const [emailCheck, setEmailCheck] = useState({
        loading: false,
        exists: false,
        checkedEmail: "",
        helperText: "",
    });

    const [loading, setLoading] = useState(false);

    const [termoAceito, setTermoAceito] = useState(false);
    const [openTermoModal, setOpenTermoModal] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    // CEP auto-complete / feedback
    const [cepStatus, setCepStatus] = useState("idle"); // idle | loading | success | error
    const [cepHelper, setCepHelper] = useState("");
    const debounceRef = useRef(null);
    const lastFetchedCepRef = useRef("");

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const onlyDigits = cep.replace(/\D/g, "");
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (onlyDigits.length === 8 && lastFetchedCepRef.current !== onlyDigits) {
            debounceRef.current = setTimeout(() => {
                fetchCep(onlyDigits);
            }, 500);
        }
    }, [cep]);

    // Lê o parâmetro de indicação ?indicacao=... da URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const refFromUrl = params.get("indicacao");

        if (refFromUrl) {
            set_indicacaoRef(refFromUrl);
            setIndicacaoBloqueada(true);

            let active = true;

            const fetchCupom = async () => {
                setCupomInfo({
                    loading: true,
                    valid: false,
                    descontoPercentual: 0,
                    indicacaoRef: refFromUrl,
                });

                try {
                    const res = await api.get(`/partner/cupom/${encodeURIComponent(refFromUrl)}`);

                    if (!active) return;

                    setCupomInfo({
                        loading: false,
                        valid: !!res.data?.valid,
                        descontoPercentual: Number(res.data?.couponDiscountPercentage || 0),
                        indicacaoRef: refFromUrl,
                    });
                } catch {
                    if (!active) return;

                    setCupomInfo({
                        loading: false,
                        valid: false,
                        descontoPercentual: 0,
                        indicacaoRef: refFromUrl,
                    });
                }
            };

            fetchCupom();

            return () => {
                active = false;
            };
        }

        setIndicacaoBloqueada(false);
        setCupomInfo({
            loading: false,
            valid: false,
            descontoPercentual: 0,
            indicacaoRef: "",
        });
    }, [location.search]);



    async function fetchCep(cepDigits) {
        try {
            setCepStatus("loading");
            setCepHelper("Buscando endereço...");
            const res = await fetch(`https://viacep.com.br/ws/${cepDigits}/json/`);
            const data = await res.json();

            if (data?.erro) {
                setCepStatus("error");
                setCepHelper("CEP não encontrado. Preencha os dados manualmente.");
                set_endereco("");
                set_bairro("");
                set_cidade("");
                set_uf("");
                lastFetchedCepRef.current = cepDigits;
                return;
            }

            set_endereco(data.logradouro || "");
            set_bairro(data.bairro || "");
            set_cidade(data.localidade || "");
            set_uf(data.uf || "");

            setCepStatus("success");
            setCepHelper("Endereço preenchido automaticamente.");
            document.getElementById("Numero")?.focus();
            lastFetchedCepRef.current = cepDigits;
        } catch (e) {
            setCepStatus("error");
            setCepHelper("Falha ao buscar CEP. Tente novamente.");
        }
    }

    async function verificarEmail(emailValue) {
        const emailNormalizado = String(emailValue || "").trim().toLowerCase();

        if (!emailNormalizado) {
            setEmailCheck({
                loading: false,
                exists: false,
                checkedEmail: "",
                helperText: "",
            });
            return false;
        }

        setEmailCheck({
            loading: true,
            exists: false,
            checkedEmail: emailNormalizado,
            helperText: "",
        });

        try {
            const res = await api.post(`/teacher/email/verify`, {
                email: emailNormalizado,
            });

            const exists = !!res.data?.exists;

            setEmailCheck({
                loading: false,
                exists,
                checkedEmail: emailNormalizado,
                helperText: exists
                    ? "Já existe uma conta com este e-mail. Faça login para continuar."
                    : "",
            });

            return exists;
        } catch {
            setEmailCheck({
                loading: false,
                exists: false,
                checkedEmail: emailNormalizado,
                helperText: "",
            });

            return false;
        }
    }

    async function validarTabDadosPessoais() {
        if (!nome || !email || !senha || !confirmar_senha || !cpf_cnpj || !celular) {
            showSnackbar("Preencha todos os campos obrigatórios dos dados pessoais.", "warning");
            return false;
        }

        const emailNormalizado = String(email || "").trim().toLowerCase();

        if (emailCheck.exists && emailCheck.checkedEmail === emailNormalizado) {
            showSnackbar("Já existe uma conta com este e-mail. Faça login para continuar.", "warning");
            return false;
        }

        const exists = await verificarEmail(emailNormalizado);

        if (exists) {
            showSnackbar("Já existe uma conta com este e-mail. Faça login para continuar.", "warning");
            return false;
        }

        const nomeOk = isNomeValido(nome) && nome.trim().length >= 3;
        if (!nomeOk) {
            showSnackbar("Nome inválido. Use apenas letras.", "warning");
            return false;
        }

        if (cpf_cnpj.length !== 14) {
            showSnackbar("CPF incompleto. Preencha no formato 000.000.000-00.", "warning");
            return false;
        }

        if (celular.length !== 15) {
            showSnackbar("Celular incompleto. Preencha no formato (00) 00000-0000.", "warning");
            return false;
        }

        if (!isPasswordValid) {
            showSnackbar("A senha deve ter no mínimo 6 caracteres, contendo letras e números.", "warning");
            return false;
        }

        if (confirmar_senha.trim() !== passwordValue) {
            showSnackbar("Senhas não conferem.", "error");
            return false;
        }

        return true;
    }

    async function handleRegister(e) {
        e.preventDefault();

        if (!(await validarTabDadosPessoais())) {
            setActiveTab(0);
            return;
        }

        if (!cep || !endereco || !numero || !bairro || !cidade || !uf) {
            showSnackbar("Preencha todos os campos obrigatórios do endereço.", "warning");
            setActiveTab(1);
            return;
        }

        if (!termoAceito) {
            showSnackbar("É obrigatório aceitar o termo de uso.", "warning");
            setActiveTab(1);
            return;
        }

        setLoading(true);

        const emailNormalizado = String(email || "").trim().toLowerCase();

        const dados = {
            name: nome.trim(),
            email: emailNormalizado,
            cpf: cpf_cnpj,
            password: passwordValue,
            zipCode: cep,
            street: endereco,
            number: numero,
            complement: complemento,
            phone: celular,
            district: bairro,
            city: cidade,
            state: uf,
            referralCode: indicacaoRef?.trim() || null,
        };

        try {
            await api.post('/teacher', dados);

            const loginRes = await api.post("/teacher/authenticate", {
                email: emailNormalizado,
                password: passwordValue,
            });

            const token = loginRes.data?.access_token || loginRes.data?.token;

            if (token) {
                try {
                    const profile = await api.get('/teacher', {
                        headers: { Authorization: `Bearer ${token}` },
                    });

                    login(normalizeTeacher(profile.data), token);
                } catch {
                    login({ name: nome.trim(), email: emailNormalizado }, token);
                }

                showSnackbar("Conta criada com sucesso! Bem-vindo(a).", "success");
                navigate("/planejamento");
                return;
            }

            showSnackbar("Conta criada, mas não foi possível autenticar automaticamente. Faça login.", "warning");
            navigate("/login");
        } catch (err) {
            const message = getApiErrorMessage(err, "Erro ao criar conta. Tente novamente.");

            if (err?.response?.status === 409 || message.toLowerCase().includes("e-mail")) {
                setActiveTab(0);
                setEmailCheck({
                    loading: false,
                    exists: true,
                    checkedEmail: emailNormalizado,
                    helperText: "Já existe uma conta com este e-mail. Faça login para continuar.",
                });

                showSnackbar("Já existe uma conta com este e-mail. Faça login para continuar.", "warning");
                return;
            }

            showSnackbar(message, "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", alignItems: "center", justifyContent: "center", py: !isMobile ? 5 : 0 }}>
            <Paper elevation={2} sx={{ p: 4, width: isMobile ? '100%' : '60%', borderRadius: !isMobile ? 4 : 0 }}>
                <Typography variant="h5" mb={2} align="center">Criar Conta</Typography>

                {cupomInfo.valid && cupomInfo.descontoPercentual > 0 && (
                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            mb: 2,
                            p: 1.6,
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
                            bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.12 : 0.10),
                        })}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: { xs: "stretch", sm: "center" },
                                justifyContent: "space-between",
                                gap: 1.2,
                            }}
                        >
                            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                <VerifiedRoundedIcon sx={{ color: "success.main", mt: 0.2 }} />

                                <Box>
                                    <Typography sx={{ fontWeight: 1000, lineHeight: 1.15 }}>
                                        Cupom aplicado:{" "}
                                        <Box component="span" sx={{ color: "success.main" }}>
                                            {cupomInfo.descontoPercentual}% OFF
                                        </Box>
                                    </Typography>

                                    <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25, lineHeight: 1.35 }}>
                                        Esse desconto será aplicado na <b>primeira mensalidade</b> da assinatura
                                        quando você escolher um plano.
                                    </Typography>
                                </Box>
                            </Box>

                            <Button
                                variant="contained"
                                color="success"
                                endIcon={<ArrowForwardRoundedIcon />}
                                onClick={() => navigate(`/assinar?indicacao=${encodeURIComponent(cupomInfo.indicacaoRef || indicacaoRef)}`)}
                                sx={{
                                    borderRadius: 999,
                                    fontWeight: 900,
                                    textTransform: "none",
                                    px: 2.2,
                                    py: 1.05,
                                    alignSelf: { xs: "stretch", sm: "center" },
                                    whiteSpace: "nowrap",
                                    display: "none"
                                }}
                            >
                                Ver planos com desconto
                            </Button>
                        </Box>
                    </Paper>
                )}


                <Tabs
                    value={activeTab}
                    onChange={async (_, v) => {
                        // impede ir para Endereço sem validar Aba 1
                        if (v === 1 && !(await validarTabDadosPessoais())) return;
                        setActiveTab(v);
                    }}
                    variant="fullWidth"
                    sx={{
                        mb: 2,
                        minHeight: 40,
                        "& .MuiTab-root": { textTransform: "none", minHeight: 40 },
                    }}
                >

                    <Tab label="Dados pessoais" />
                    <Tab label="Endereço" />
                </Tabs>

                <form onSubmit={handleRegister}>
                    {/* Aba 1 - Dados pessoais */}
                    {activeTab === 0 && (
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Nome"
                                        fullWidth
                                        required
                                        value={nome}
                                        onChange={(e) => set_nome(onlyLettersSpaces(e.target.value))}
                                        onBlur={() => set_nome((prev) => toTitleCaseNome(prev))}
                                        disabled={loading}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="CPF"
                                        placeholder="Apenas para controle interno"
                                        fullWidth
                                        required
                                        value={cpf_cnpj}
                                        onChange={e => set_cpf_cnpj(mascaraCpfCnpj(e.target.value))}
                                        inputProps={{ maxLength: 14 }}
                                        disabled={loading}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Celular"
                                        fullWidth
                                        required
                                        value={celular}
                                        onChange={e => set_celular(mascaraCelular(e.target.value))}
                                        inputProps={{ maxLength: 16 }}
                                        disabled={loading}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="E-mail"
                                        type="email"
                                        fullWidth
                                        required
                                        value={email}
                                        onChange={(e) => {
                                            set_email(e.target.value);
                                            setEmailCheck({
                                                loading: false,
                                                exists: false,
                                                checkedEmail: "",
                                                helperText: "",
                                            });
                                        }}
                                        onBlur={async () => {
                                            const emailNormalizado = String(email || "").trim().toLowerCase();
                                            if (!emailNormalizado) return;

                                            set_email(emailNormalizado);
                                            await verificarEmail(emailNormalizado);
                                        }}
                                        disabled={loading}
                                        error={emailCheck.exists}
                                        helperText={emailCheck.helperText}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {emailCheck.loading ? <CircularProgress size={18} /> : null}
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Senha"
                                        type="password"
                                        fullWidth
                                        required
                                        value={senha}
                                        onChange={e => set_senha(e.target.value)}
                                        disabled={loading}
                                        error={senha.length > 0 && !isPasswordValid}
                                        helperText={passwordHelperText}
                                        FormHelperTextProps={{
                                            sx: {
                                                color: senha.length > 0 && isPasswordValid ? "success.main" : undefined,
                                                fontWeight: senha.length > 0 ? 600 : 400,
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Confirmar Senha"
                                        type="password"
                                        fullWidth
                                        required
                                        value={confirmar_senha}
                                        onChange={e => set_confirmar_senha(e.target.value)}
                                        disabled={loading}
                                        error={confirmar_senha.length > 0 && confirmar_senha.trim() !== passwordValue}
                                        helperText={
                                            confirmar_senha.length > 0 && confirmar_senha.trim() !== passwordValue
                                                ? "As senhas não conferem."
                                                : ""
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        label="Código de indicação (opcional)"
                                        fullWidth
                                        value={indicacaoRef}
                                        onChange={(e) => {
                                            set_indicacaoRef(e.target.value.trim());

                                            if (!e.target.value.trim()) {
                                                setCupomInfo({
                                                    loading: false,
                                                    valid: false,
                                                    descontoPercentual: 0,
                                                    indicacaoRef: "",
                                                });
                                            }
                                        }}
                                        disabled={loading || indicacaoBloqueada}
                                        helperText={
                                            indicacaoBloqueada
                                                ? "Código aplicado automaticamente a partir do link de indicação."
                                                : "Se você recebeu um código de um influenciador, informe aqui."
                                        }
                                        InputProps={{
                                            endAdornment: !isMobile ? (
                                                <InputAdornment position="end">
                                                    {cupomInfo.loading ? (
                                                        <CircularProgress size={18} />
                                                    ) : cupomInfo.valid ? (
                                                        <Chip
                                                            size="small"
                                                            color="success"
                                                            icon={<DiscountIcon fontSize="small" sx={{ verticalAlign: "middle" }} />}
                                                            label={`Cupom aplicado • ${cupomInfo.descontoPercentual}% OFF`}
                                                            sx={{
                                                                fontWeight: 800,
                                                                maxWidth: 230,
                                                                "& .MuiChip-label": {
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                },
                                                            }}
                                                        />
                                                    ) : null}
                                                </InputAdornment>
                                            ) : null,
                                        }}
                                        onBlur={async () => {
                                            const ref = indicacaoRef?.trim();

                                            if (!ref) {
                                                setCupomInfo({
                                                    loading: false,
                                                    valid: false,
                                                    descontoPercentual: 0,
                                                    indicacaoRef: "",
                                                });
                                                return;
                                            }

                                            setCupomInfo({
                                                loading: true,
                                                valid: false,
                                                descontoPercentual: 0,
                                                indicacaoRef: ref,
                                            });

                                            try {
                                                const res = await api.get(`/partner/cupom/${encodeURIComponent(ref)}`);

                                                setCupomInfo({
                                                    loading: false,
                                                    valid: !!res.data?.valid,
                                                    descontoPercentual: Number(res.data?.couponDiscountPercentage || 0),
                                                    indicacaoRef: ref,
                                                });
                                            } catch {
                                                setCupomInfo({
                                                    loading: false,
                                                    valid: false,
                                                    descontoPercentual: 0,
                                                    indicacaoRef: ref,
                                                });
                                            }
                                        }}
                                    />

                                    {isMobile && cupomInfo.loading && (
                                        <Box sx={{ mt: 1 }}>
                                            <CircularProgress size={18} />
                                        </Box>
                                    )}

                                    {isMobile && cupomInfo.valid && !cupomInfo.loading && (
                                        <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-start" }}>
                                            <Chip
                                                size="small"
                                                color="success"
                                                icon={<DiscountIcon fontSize="small" sx={{ verticalAlign: "middle" }} />}
                                                label={`Cupom aplicado • ${cupomInfo.descontoPercentual}% OFF`}
                                                sx={{
                                                    fontWeight: 800,
                                                    maxWidth: "100%",
                                                    "& .MuiChip-label": {
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* Aba 2 - Endereço */}
                    {activeTab === 1 && (
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="CEP"
                                        fullWidth
                                        required
                                        value={cep}
                                        onChange={e => set_cep(mascaraCEP(e.target.value))}
                                        inputProps={{ maxLength: 9 }}
                                        disabled={loading}
                                        inputMode="numeric"
                                        helperText={cepHelper}
                                        FormHelperTextProps={{
                                            sx: { color: cepStatus === "error" ? "error.main" : "text.secondary" },
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {cepStatus === "loading" ? (
                                                        <CircularProgress size={18} />
                                                    ) : cepStatus === "success" ? (
                                                        <Chip size="small" color="success" label="OK" sx={{ fontWeight: 800, cursor: "default" }} />
                                                    ) : cepStatus === "error" ? (
                                                        <Chip size="small" color="error" label="!" sx={{ fontWeight: 800, cursor: "default" }} />
                                                    ) : null}
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="Numero"
                                        label="Número"
                                        fullWidth
                                        required
                                        value={numero}
                                        onChange={e => set_numero(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        inputProps={{ inputMode: "numeric" }}
                                        disabled={loading}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        label="Endereço"
                                        fullWidth
                                        required
                                        value={endereco}
                                        onChange={e => set_endereco(e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Bairro"
                                        fullWidth
                                        required
                                        value={bairro}
                                        onChange={e => set_bairro(e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Cidade"
                                        fullWidth
                                        required
                                        value={cidade}
                                        onChange={e => set_cidade(e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={2}>
                                    <TextField
                                        label="UF"
                                        fullWidth
                                        required
                                        value={uf}
                                        onChange={e => set_uf(e.target.value.toUpperCase().slice(0, 2))}
                                        inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                                        disabled={loading}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        label="Complemento (opcional)"
                                        fullWidth
                                        value={complemento}
                                        onChange={e => set_complemento(e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* Checkbox do termo de uso - só na última aba */}
                    {activeTab === 1 && (
                        <FormControlLabel
                            sx={{ alignItems: "center", mt: 2, mb: 0.5, pl: 0.7 }}
                            control={
                                <Checkbox
                                    checked={termoAceito}
                                    onChange={e => setTermoAceito(e.target.checked)}
                                    disabled={loading}
                                    color="primary"
                                    sx={{ p: 0.5, mt: 0.2 }}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Typography variant="body2" component="span">
                                        Declaro que li e aceito o{" "}
                                        <span
                                            style={{
                                                color: "#1976d2",
                                                cursor: "pointer",
                                                fontWeight: 600,
                                                textDecoration: "underline"
                                            }}
                                            onClick={e => {
                                                e.preventDefault();
                                                setOpenTermoModal(true);
                                            }}
                                        >
                                            termo de uso
                                        </span>
                                        .
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        component="span"
                                        sx={{ ml: 0.5, fontWeight: 700 }}
                                    >
                                        *
                                    </Typography>
                                </Box>
                            }
                        />
                    )}

                    {/* Botões de navegação / ação */}
                    <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 1, mt: 2, width: "100%" }}>
                        {activeTab === 0 && (
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={async () => {
                                    if (await validarTabDadosPessoais()) setActiveTab(1);
                                }}
                                disabled={loading}
                                sx={{
                                    mt: 1,
                                    mb: 1,
                                    py: 1.2,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    fontSize: 15,
                                    textTransform: "none"
                                }}
                            >
                                Avançar
                            </Button>
                        )}

                        {activeTab === 1 && (
                            <>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => setActiveTab(0)}
                                    disabled={loading}
                                    sx={{
                                        mt: 1,
                                        mb: 1,
                                        py: 1.2,
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        fontSize: 15,
                                        textTransform: "none"
                                    }}
                                >
                                    Voltar
                                </Button>

                                <Button
                                    fullWidth
                                    variant="contained"
                                    type="submit"
                                    sx={{
                                        position: 'relative',
                                        mt: 1,
                                        mb: 1,
                                        py: 1.2,
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        fontSize: 15,
                                        textTransform: "none",
                                        '&.Mui-disabled': {
                                            backgroundColor: 'rgba(160,160,160,0.6)',
                                            color: '#fff',
                                            cursor: 'not-allowed',
                                        }
                                    }}
                                    disabled={loading}
                                >
                                    {loading && (
                                        <CircularProgress
                                            size={20}
                                            sx={{ color: "white", position: "absolute", left: 16 }}
                                        />
                                    )}
                                    <span style={{ marginLeft: loading ? 24 : 0 }}>
                                        {loading ? "Criando..." : "Criar conta"}
                                    </span>
                                </Button>
                            </>
                        )}
                    </Box>


                </form>

                {/* Modal termo de uso */}
                <Dialog
                    open={openTermoModal}
                    onClose={() => setOpenTermoModal(false)}
                    scroll="paper"
                    fullWidth
                    maxWidth="sm"
                    fullScreen={isMobile}
                    PaperProps={{
                        sx: {
                            p: 0,
                            borderRadius: isMobile ? 0 : 3
                        }
                    }}
                >
                    <DialogTitle>Termo de Uso
                        <IconButton onClick={() => setOpenTermoModal(false)} sx={{ position: "absolute", right: 8, top: 10 }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers sx={{ overflowY: 'auto' }}>
                        <TermoUsoConteudo />
                    </DialogContent>
                    <DialogActions sx={{
                        p: 2,
                        position: isMobile ? "fixed" : "static",
                        bottom: isMobile ? 0 : "auto",
                        left: isMobile ? 0 : "auto",
                        right: isMobile ? 0 : "auto",
                        bgcolor: isMobile ? "background.paper" : "transparent",
                        borderTop: isMobile ? `1px solid ${theme.palette.divider}` : "none",
                        zIndex: 10
                    }}>
                        <Button onClick={() => setOpenTermoModal(false)} color="primary" variant="outlined">
                            Fechar
                        </Button>
                    </DialogActions>
                </Dialog>

                <Divider sx={{ my: 2.5 }} />

                <Typography align="center" variant="body2" mt={2}>
                    Já tem uma conta?{" "}
                    <Link component="button" underline="hover" onClick={() => navigate("/login")}>
                        Entrar
                    </Link>
                </Typography>
            </Paper>
        </Box>
    );
}